import { useRef } from '@frontend/react';
import type { ExtractTaskInitialQuestionWithAnswer } from '@frontend/react-domain';
import { CheckboxInput, TextareaInput, YStack } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';

export type CheckboxIQProps = {
  initialQuestionWithAnswer: ExtractTaskInitialQuestionWithAnswer<'multi_choice'>;
  isLoading: boolean;
  onChange: (answer: ExtractTaskInitialQuestionWithAnswer<'multi_choice'>['answer']) => void;
};

export const CheckboxIQ = ({ initialQuestionWithAnswer, isLoading, onChange }: CheckboxIQProps) => {
  const textAreaInputRef = useRef<HTMLTextAreaElement>(null);
  const onPressChoice = (choice: string) => () => {
    onChange(
      initialQuestionWithAnswer.answer.find((a) => a.id === choice)
        ? initialQuestionWithAnswer.answer.filter((a) => a.id !== choice)
        : initialQuestionWithAnswer.answer.concat({ id: choice, value: choice === 'Other' ? '' : choice }),
    );
  };

  const onUpdateOtherFreeResponseChoice = (choiceText: string) => {
    onChange(initialQuestionWithAnswer.answer.map((o) => (o.id === 'Other' ? { ...o, value: choiceText } : o)));
  };

  const otherOption = initialQuestionWithAnswer.answer.find((o) => o.id === 'Other');

  return (
    <YStack gapY="sm">
      {initialQuestionWithAnswer.choices.map((choice) => {
        const showOtherTextInput = otherOption && choice === 'Other';
        const isSelected = !!initialQuestionWithAnswer.answer.find((a) => a.id === choice);
        return (
          <YStack gap="sm" key={choice}>
            <CheckboxInput disabled={isLoading} label={choice} onChange={onPressChoice(choice)} value={isSelected} />
            {showOtherTextInput ? (
              <YStack
                bg="neutral50"
                className="cursor-pointer"
                disabled={isLoading}
                justifyContent="center"
                onClick={() => textAreaInputRef.current?.focus()}
                p="md"
                radius="lg"
              >
                <TextareaInput
                  classNames={{
                    input: safeCss('bg-neutral-50 border-none text-p-md p-none'),
                  }}
                  disabled={isLoading}
                  label=""
                  maxRows={2}
                  minRows={1}
                  onChange={onUpdateOtherFreeResponseChoice}
                  placeholder="Type anything about your task"
                  ref={textAreaInputRef}
                  value={otherOption?.value || ''}
                />
              </YStack>
            ) : null}
          </YStack>
        );
      })}
    </YStack>
  );
};
